import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { STORAGE_KEYS } from '../constants/storage.const';

/**
 * Get auth token from localStorage
 */
function getAuthToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
}

/**
 * Add Authorization header to request if token exists
 * Also sets withCredentials to true for cookie-based auth support
 */
function addAuthHeader(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = getAuthToken();

    if (!token) {
        return req.clone({
            withCredentials: true
        });
    }

    return req.clone({
        setHeaders: {
            Authorization: `Bearer ${token}`
        },
        withCredentials: true
    });
}

/**
 * Handle HTTP errors, specifically 401 Unauthorized
 */
function handleAuthError(error: HttpErrorResponse, router: Router): void {
    if (error.status === 401) {
        // Clear stored token
        localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);

        // Redirect to login
        router.navigate(['/auth']);
    }
}

/**
 * HTTP interceptor that adds Bearer token to requests and handles 401 responses
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const router = inject(Router);

    // Clone request with auth header if token exists
    const authReq = addAuthHeader(req);

    // Handle the request and catch errors
    return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
            handleAuthError(error, router);
            return throwError(() => error);
        })
    );
};
